import { Link, Navigate, useParams } from "react-router-dom";
import { useClassrooms } from "../data/ClassroomContext";
import { useLessons } from "../data/LessonsContext";
import useProgress from "../data/useProgress";
import { COMPLETED } from "../data/progressStore";
import { BackIcon, CheckIcon } from "../components/Icons";
import {
  LessonAction,
  LessonMeta,
  orderForToday,
} from "../components/LessonState";
import { LoadingCards, Message, ProblemMessage } from "../components/Status";
import ui from "../components/ui.module.css";
import { teachingContext } from "./Week";

// One subject's lessons for the week being taught: the next one first.
const SubjectLessons = () => {
  const { subject: subjectKey } = useParams();
  const lessons = useLessons();
  const progress = useProgress();
  const classes = useClassrooms();

  if (lessons.status === "ready" && !lessons.week) return <Navigate to="/" replace />;

  const back = (
    <Link to="/week" className={ui.textButton}>
      <BackIcon />
      {lessons.week ? `Week ${lessons.week}` : "Week"}
    </Link>
  );

  if (lessons.status !== "ready") {
    return (
      <div className={ui.page}>
        {back}
        {lessons.status === "loading" ? (
          <LoadingCards />
        ) : (
          <ProblemMessage problem={lessons.problem} onRetry={lessons.retry} autoRetrying={lessons.autoRetrying} />
        )}
      </div>
    );
  }

  const subject = lessons.findSubject(subjectKey);
  if (!subject) {
    return (
      <div className={ui.page}>
        {back}
        <Message
          title={`Nothing for this subject in Week ${lessons.week}`}
          actions={
            <Link to="/week" className={`${ui.button} ${ui.primary}`}>
              Choose a subject
            </Link>
          }
        >
          It may be in another week, or not approved for {classes.active.name} yet.
        </Message>
      </div>
    );
  }

  const ordered = orderForToday(subject.lessons, progress.get);
  const [next, ...later] = ordered;
  const allDone = progress.get(next.id)?.status === COMPLETED;
  const listed = allDone ? ordered : later;

  return (
    <div className={ui.page}>
      {back}
      <div className={ui.stack}>
        <h1 className={ui.title}>{subject.name}</h1>
        <p className={ui.eyebrow}>
          {teachingContext({ className: classes.active.name, week: lessons.week })}
        </p>
      </div>

      {allDone ? (
        <div className={`${ui.card} ${ui.stack}`}>
          <span className={`${ui.state} ${ui.done}`}>
            <CheckIcon />
            All done
          </span>
          <h2 className={ui.cardTitle}>
            You've completed every Week {lessons.week} {subject.name} lesson.
          </h2>
        </div>
      ) : (
        <section
          className={`${ui.card} ${ui.featured} ${ui.stack}`}
          aria-labelledby="up-next"
        >
          <p id="up-next" className={ui.eyebrow}>
            Up next{next.studyArea ? ` · ${next.studyArea}` : ""}
          </p>
          <h2 className={ui.cardTitle}>{next.title}</h2>
          <LessonMeta lesson={next} />
          <LessonAction lesson={next} record={progress.get(next.id)} primary />
        </section>
      )}

      {listed.length > 0 && (
        <section className={ui.stack} aria-labelledby="more-lessons">
          <h2 id="more-lessons" className={ui.eyebrow}>
            {allDone ? `Week ${lessons.week}` : `More ${subject.name} this week`}
          </h2>
          <ul className={ui.list}>
            {listed.map((lesson) => (
              <li key={lesson.id} className={`${ui.card} ${ui.row}`}>
                <div className={ui.stack} style={{ gap: "0.25rem" }}>
                  {lesson.studyArea && (
                    <span className={ui.eyebrow}>{lesson.studyArea}</span>
                  )}
                  <h3 className={ui.cardTitle} style={{ fontSize: "1rem" }}>
                    {lesson.title}
                  </h3>
                  <LessonMeta lesson={lesson} />
                </div>
                <LessonAction lesson={lesson} record={progress.get(lesson.id)} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default SubjectLessons;
